<script setup lang="ts">
    import { formatAmount, formatDate } from '@/utils';
    import { useRoute } from 'vue-router';
    import { onMounted, reactive, ref } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import axios from 'axios';
    import MarketCommandeRejectModal from './modals/MarketCommandeRejectModal.vue';

    const route = useRoute();
    const commande = reactive({
        num_commande: "",
        created_at: "",
        sous_total: 0,
        total: 0,
        statut: "",
        user: {
            nom: "",
            prenom: "",
            email: "",
        },
        produits: [] as any[],
    });
    const loading = ref(false);
    const errorMessage = ref('');
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const rejectModalRef = ref<InstanceType<typeof MarketCommandeRejectModal> | null>(null);

    onMounted(() => {
        Object.assign(commande, route.meta.commande);
    });

    async function doValidateCommande(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Commande Market", "Voulez-vous vraiment valider cette commande ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                const result = await axios.post(`/backoffice/market-commandes/validate/${item.id}`, {});
                Object.assign(commande, result.data);
            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    async function doRejectCommande(item: any) {
        const result: any = await rejectModalRef.value?.open(item);
        if (result) {
            Object.assign(commande, result);
        }
    }

    async function doStartDelivery(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Commande Market", "Confirmez-vous le début de la livraison de cette commande ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                const result = await axios.post(`/backoffice/market-commandes/start-delivery/${item.id}`, {});
                Object.assign(commande, result.data);
            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }
</script>
<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>
        
        <h2 class="secondary-font gap-4 mt-2 mb-6">
            Détails commande - {{ commande.num_commande }}
        </h2>

        <div class="clear-both h-0"></div>

        <div class="s20 widget pt-8 pb-8">
            <div class="s10">
                &nbsp;
            </div>
            <div class="s10 mt--5-5 flex-right" v-if="commande.statut == 'pending'">
                <custom-button :loading="loading" class="btn" @click="doValidateCommande(commande)">
                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                    Valider la commande
                </custom-button>
                <custom-button :loading="loading" class="btn danger mr-4" @click="doRejectCommande(commande)">
                    <img src="/icons/icon-danger.svg" class="btn-icon" alt="" />
                    Rejeter la commande
                </custom-button>
            </div>
            <div class="s10 mt--5-5 flex-right" v-if="commande.statut == 'confirmed'">
                <custom-button :loading="loading" class="btn" @click="doStartDelivery(commande)">
                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                    Procéder à la livraison
                </custom-button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="flex px-6">
                <div class="s3_mobile px-4">
                    <div class="rounded-100 overflow-hidden item active"><!--active-->
                        <img :src="'/assets/users/300w/user2.jpg'" width="100%" />
                    </div>
                </div>

                <div class="s1_mobile flex-center">&nbsp;</div>
                
                <div class="s4_mobile">
                    <h3 class="my-2">{{ commande.user.nom + ' ' + commande.user.prenom }}</h3>
                    <h6 class="font-normal my-2">{{ commande.user.email }}</h6>
                </div>


                <div class="s1_mobile flex-center">
                    <hr class="hr-vertical h-90p"/>
                </div>

                <div class="s9_mobile px-8">
                    <div class="text-xl text-grey-600 my-2">Num Commande: <div class="text-primary-color">{{ commande.num_commande }}</div></div>
                    <div class="text-xl text-grey-600 my-2">Date Commande: <div class="">{{ formatDate(commande.created_at) }}</div></div>
                    <div class="text-xl text-grey-600 my-2">Sous total: <div class="">{{ formatAmount(commande.sous_total) }}</div></div>
                    <div class="text-xl text-grey-600 my-2">Total: <div class="">{{ formatAmount(commande.total) }}</div></div>
                    <div class="text-xl text-grey-600 my-2">Status: <div class="">{{ commande.statut }}</div></div>
                </div>
                
            </div>

            <div class="clear-both h-30"></div>
            <hr class="s20 my-0 hr2 opacity-1"/>
            <div class="clear-both h-30"></div>
            
            <table class="table separator skin2 selection">
                <!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  -->
                <thead>
                    <tr> 
                        <th width="80px"></th>
                        <th>Produit</th>
                        <th>Prix unitaire</th>
                        <th>Quantité</th>
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="commande.produits.length == 0">
                        <tr>
                            <td colspan="5">
                                <div class="text-center text-grey-700">Aucun élément trouvé</div>
                            </td>
                        </tr>
                    </template>
                    <tr class="text-grey-700 over-effect" v-for="(item, index) in commande.produits" :key="'item_' + index"><!-- over-effect active-->
                        <td>
                            <div class="square-lg px-3 py-3" style="background-color: rgb(var(--color-success-rgb), 0.1);">
                                <img :src="item.produit.image_url ? item.produit.image_url : '/icons/icon-securite-role.svg'" width="100%" />
                            </div>
                        </td>
                        <td><h6>{{ item.produit.nom }}</h6></td>
                        <td><h6>{{ formatAmount(item.montant_apres_remises) }}</h6></td>
                        <td><h6>{{ formatAmount(item.quantite) }}</h6></td>
                        <td><h6>{{ formatAmount(item.total) }}</h6></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
        <market-commande-reject-modal ref="rejectModalRef"></market-commande-reject-modal>

    </section>
</template>